/* Magic In Home — base interactions
 * Vanilla, no dependencies. Loaded with `defer`. */

(function () {
  'use strict';

  // ---------- Cart count bubble — live update on add to cart ----------
  // The bubble is server-rendered only when cart is non-empty. After any
  // add-to-cart action we fetch /cart.js and (a) create the bubble if missing
  // (b) update its count (c) remove it if cart is now empty.
  async function refreshCartBubble() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      const count = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;

      // Cart icon anchors used by Dawn-based themes
      const cartIcons = document.querySelectorAll('a.header__icon--cart');
      cartIcons.forEach((icon) => {
        let bubble = icon.querySelector('.cart-count-bubble');
        if (count > 0) {
          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            icon.appendChild(bubble);
          }
          const display = count < 100 ? String(count) : '99+';
          bubble.innerHTML = '<span aria-hidden="true">' + display + '</span>'
            + '<span class="visually-hidden">' + count + ' items</span>';
        } else if (bubble) {
          bubble.remove();
        }
      });
    } catch (e) {
      // Silent fail — don't break the page if cart API hiccups.
      if (window.console && console.warn) console.warn('[MIH] cart bubble refresh failed:', e);
    }
  }

  // Hook 1: Form submissions to /cart/add.js (or /cart/add)
  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.action) return;
    if (/\/cart\/add(\.js)?$/i.test(form.action)) {
      // Slight delay so server processes the add first.
      setTimeout(refreshCartBubble, 600);
    }
  }, true);

  // Hook 2: Clicks on common Add-to-Cart buttons (covers AJAX adds too).
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('button[name="add"], [data-mih-atc], button[type="submit"]');
    if (!btn) return;
    const form = btn.closest('form');
    if (!form) return;
    const action = form.action || '';
    if (!/\/cart\/add(\.js)?/i.test(action)) return;
    setTimeout(refreshCartBubble, 800);
  }, true);

  // Hook 3: Listen for Shopify cart drawer / theme events (some themes dispatch these)
  ['cart:added', 'cart:updated', 'cart:refresh', 'cart-drawer:open'].forEach(function (evt) {
    document.addEventListener(evt, refreshCartBubble);
  });

  // Hook 4: Catch fetch-based add-to-cart calls (modern Dawn AJAX flow) by
  // wrapping window.fetch and pinging refresh after any /cart/* mutation.
  if (typeof window.fetch === 'function' && !window.__mihFetchPatched) {
    const origFetch = window.fetch;
    window.__mihFetchPatched = true;
    window.fetch = function (input, init) {
      const url = (typeof input === 'string') ? input : (input && input.url) || '';
      const promise = origFetch.apply(this, arguments);
      if (/\/cart\/(add|update|change|clear)(\.js)?/i.test(url)) {
        promise.then(function () { setTimeout(refreshCartBubble, 200); }).catch(function () {});
      }
      return promise;
    };
  }


  // ---------- Reveal on scroll (staggered) ----------
  const revealEls = document.querySelectorAll('.mih-reveal');
  if (revealEls.length && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    revealEls.forEach((el, i) => {
      el.style.transitionDelay = (i % 6) * 80 + 'ms';
      io.observe(el);
    });
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  // ---------- Smooth scroll for in-page anchors ----------
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // ---------- Marquee / ticker pause on hover (USP strip) ----------
  document.querySelectorAll('[data-mih-marquee]').forEach((track) => {
    track.addEventListener('mouseenter', () => track.style.animationPlayState = 'paused');
    track.addEventListener('mouseleave', () => track.style.animationPlayState = 'running');
  });

  // ---------- Quantity steppers ----------
  document.querySelectorAll('[data-mih-qty]').forEach((wrap) => {
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    wrap.querySelectorAll('[data-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.step, 10) || 1;
        const min = parseInt(input.min || '1', 10);
        const max = parseInt(input.max || '999', 10);
        const next = Math.max(min, Math.min(max, (parseInt(input.value, 10) || min) + step));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  // ---------- MIH-style hide on scroll-down, reveal on scroll-up ----------
  // The header is already position: sticky via CSS. We just toggle the
  // .mih-header-hidden class based on scroll direction; the CSS handles the
  // smooth animation by transitioning the `top` property.
  function initMihScrollHeader() {
    // Clean up leftovers from older versions of this code.
    document.querySelectorAll('.mih-scroll-header-spacer').forEach(function (el) {
      el.parentNode && el.parentNode.removeChild(el);
    });
    document.querySelectorAll('.mih-scroll-header').forEach(function (el) {
      el.classList.remove('mih-scroll-header', 'is-hidden', 'is-scrolled');
    });

    const header = document.querySelector('.section-header');
    if (!header || header.dataset.mihHideOnScrollInit === 'true') return;
    header.dataset.mihHideOnScrollInit = 'true';

    // Publish header height so CSS can use it for the hide-translate.
    function syncHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight, { passive: true });

    let lastY = window.scrollY;
    let ticking = false;
    const SHOW_AFTER_DELTA = 4;     // px scrolled up before reveal
    const HIDE_AFTER_DELTA = 6;     // px scrolled down before hide
    const REVEAL_NEAR_TOP = 80;     // always revealed when within this far from top

    function onScroll() {
      const y = window.scrollY;
      const delta = y - lastY;

      if (y < REVEAL_NEAR_TOP) {
        header.classList.remove('mih-header-hidden');
      } else if (delta > HIDE_AFTER_DELTA) {
        header.classList.add('mih-header-hidden');
      } else if (delta < -SHOW_AFTER_DELTA) {
        header.classList.remove('mih-header-hidden');
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    }, { passive: true });
  }

  // ---------- Category circles: keep them in normal page flow ----------
  // Previously we mounted the circles into the sticky header rail to keep
  // the header alignment. That made the page feel like it "started at the
  // circles" because the header+circles stayed locked while the hero
  // scrolled away. We now leave the circles in their own section so they
  // scroll naturally below the header.
  function initMihCirclesLayout() {
    initMihScrollHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMihCirclesLayout);
  } else {
    initMihCirclesLayout();
  }
  window.addEventListener('load', initMihCirclesLayout);
  document.addEventListener('shopify:section:load', initMihCirclesLayout);

  // ---------- Header shadow on scroll ----------
  const header = document.querySelector('.section-header .header-wrapper, .section-header sticky-header');
  const headerGroup = document.querySelector('.shopify-section-group-header-group');
  if (header) {
    const onScroll = () => {
      const scrolled = window.scrollY > 8;
      if (headerGroup) headerGroup.classList.toggle('is-scrolled', scrolled);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // ---------- Login modal (DISABLED — store uses Shopify New Customer Accounts) ----------
  // New customer accounts use magic-link / passwordless auth and live at
  // shopify.com/<store_id>/account. Our old email+password modal posted to
  // /account/login which 404s under the new system. We hide the modal HTML
  // and let Shopify's native account icon navigate to the new flow.
  const loginModal = document.querySelector('[data-mih-login-modal]');
  if (loginModal) {
    loginModal.hidden = true;
    loginModal.remove();
  }
})();
